import React, { useCallback, useState } from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@core-ui/Components/ui/dialog.tsx';
import { useTranslation } from 'react-i18next';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { Input } from '@core-ui/Components/ui/input.tsx';
import { IntegrationMappingModalModes } from '@features/Settings/enums/IntegrationsEnums.ts';
import { InputSelect, InputSelectTrigger } from '@ui/shared/customSelect.tsx';
import { Label } from '@core-ui/Components/ui/label.tsx';
import { getCurrentUser } from '@features/auth';
import {
  type ApiResponse,
  type SelectOption,
  type HubspotFormattedDisposition,
  type CreateDispositionDto,
  type FormattedDisposition,
  type IntegrationDisposition,
  type Mapping,
  type HubspotMapping,
  type OutreachDisposition,
  type UpdateDispositionDto, Outcome
} from '@types';

import { logError } from '@lib/logger/logError.ts';
import { logSuccess } from '@lib/logger/logSuccess.tsx';
import { z } from 'zod';

type OverwriteDto = CreateDispositionDto | UpdateDispositionDto | { dispositionIdToDelete: number };

interface OverwriteMappingDialogProps {
    trigger: React.JSX.Element;
    mode: IntegrationMappingModalModes,
    nonce: string,
    refreshCallback: (organizationId: string) => Promise<ApiResponse<Mapping>> | Promise<ApiResponse<HubspotMapping>>,
    dispositionId?: number,
    currentDisposition?: FormattedDisposition | HubspotFormattedDisposition,
    mappingValue?: string,
    createCallback?:(organizationId: string, nonce: string, createOutreachMappingDto: CreateDispositionDto)=> Promise<ApiResponse<OutreachDisposition>>
    updateCallback?:(organizationId: string, nonce: string, updateOutreachMappingDto: UpdateDispositionDto)=> Promise<ApiResponse<OutreachDisposition>>
    deleteCallback?:(organizationId: string, nonce: string, callDispositionId: number)=> Promise<ApiResponse<void>>
}

/**
 * A generic modal to create, update or delete an integration disposition, contains standard messages for any integration account.
 */
export function MappingDialog ({ 
  trigger, mode, nonce, refreshCallback,
  dispositionId,  currentDisposition,
  mappingValue, createCallback, 
  updateCallback, deleteCallback
}: OverwriteMappingDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  const [dispositionName, setDispositionName] = useState('');
  const [selectedCreateType, setCreateSelectedUpdateType] = useState('');
  const [selectedUpdateType, setUpdateSelectedUpdateType] = useState(currentDisposition?.outcome ?? '');
  const [inputChanged, setInputChanged] = useState(false);
  const [selectChanged, setSelectChanged] = useState(false);
  const [validationError, setValidationError] = useState({ name: '', outcome: '' });

  const dialogContainerStyles = `rounded-2xl w-full max-w-lg border-background',
    'data-[state=open]:animate-in data-[state=open]:fade-in-0', 'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
    '[&_div[data-radix-dialog-overlay]]:bg-black/60', '[&_div[data-radix-dialog-overlay]]:backdrop-blur-sm`;

  const editOrCreateSchema = z.object({
    name: z.string().min(1, t('settings.integrationsPages.mappings.modalNameValidationError')),
    outcome: z.string().regex(/^(completed|no_answer)$/, t('settings.integrationsPages.mappings.modalTypeValidationError')),
  });
  
  const outcomeMap = [
    { value: Outcome.COMPLETED, label: 'Completed' },
    { value: Outcome.NO_ANSWER, label: 'No answer' }
  ];
  
  const dispositionTypeOptions = outcomeMap.reduce((options, type)=>{
    const { value, label } = type;
    options.push( { value, label });
    return options;
  }, new Array<SelectOption>());
  
  const validateField = useCallback((field: 'name' | 'outcome', value: string) => {
    const partial = editOrCreateSchema.pick({ [field]: true });
    const result = partial.safeParse({ [field]: value });
    setValidationError((prev) => ({
      ...prev,
      [field]: result.success ? undefined : result.error.errors[0]?.message,
    }));
   
  }, [editOrCreateSchema]);

  async function toastTransactionResponse<T = Partial<IntegrationDisposition>> (response: ApiResponse<T>, payload: OverwriteDto, mode: IntegrationMappingModalModes): Promise<void> {
    let definedAction;
    switch  (mode) {
    case IntegrationMappingModalModes.Create: definedAction = t('settings.integrationsPages.mappings.toastCreated'); break;
    case IntegrationMappingModalModes.Edit: definedAction = t('settings.integrationsPages.mappings.toastUpdated'); break;
    case IntegrationMappingModalModes.Delete: definedAction = t('settings.integrationsPages.mappings.toastDeleted'); break;
    }

    if (response.data === null || response.data === undefined) {
      await logError(
        t('settings.integrationsPages.mappings.errorToastMessage', { action: definedAction }),
        mode.toString().concat('disposition'),
        { payloadSent: payload }, true);
      return;
    }
    await logSuccess(
      t('settings.integrationsPages.mappings.successToastMessage', { action: definedAction }),
      mode.toString().concat('disposition'),
      { payloadSent: payload }, true);
  }
  
  function handleNameInput (event: React.FormEvent<HTMLInputElement>): void {
    setInputChanged(true);
    if (event.target !== null) {
      const name = (event.target as HTMLInputElement).value;
      setDispositionName(name);
    }
    validateField('name', (event.target as HTMLInputElement).value);
  }
  
  async function createDisposition (
    createCallback? :(
        organizationId: string,
        nonce: string,
        createOutreachMappingDto: CreateDispositionDto)=> Promise<ApiResponse<OutreachDisposition>>
  ): Promise<void> {
    if (createCallback != null) {
      const currentUser = getCurrentUser();
      if (currentUser === null) {
        throw new Error('No current user found in session store.');
      }
      const createPayload: CreateDispositionDto = {
        data: {
          type: 'callDisposition',
          attributes: {
            name: dispositionName,
            outcome: selectedCreateType
          }
        }
      };
      const { organizationId } = currentUser;
      const response = await createCallback(organizationId, nonce, createPayload);

      await toastTransactionResponse(response, createPayload, mode);

      await refreshCallback(currentUser.organizationId);
    }
  }
  
  async function updateDisposition (
    updateCallback? :(
        organizationId: string,
        nonce: string,
        updateOutreachMappingDto: UpdateDispositionDto)=> Promise<ApiResponse<OutreachDisposition>>
  ): Promise<void> {
    if (updateCallback != null && dispositionId !== undefined) {
      const currentUser = getCurrentUser();
      if (currentUser === null) {
        throw new Error('No current user found in session store.');
      }

      const payload: { data: IntegrationDisposition } = {
        data: {
          id: dispositionId,
          type: 'callDisposition',
          attributes: {
            name: dispositionName,
            outcome: selectedUpdateType
          }
        }
      };
      const { organizationId } = currentUser;

      const response = await updateCallback(organizationId, nonce, payload);

      await toastTransactionResponse(response, payload, mode);

      await refreshCallback(currentUser.organizationId);
    }
  }

  async function deleteDisposition (
    deleteCallback? :(
            organizationId: string,
            nonce: string,
            callDispositionId: number)=> Promise<ApiResponse<void>>
  ): Promise<void> {
    if (deleteCallback != null && dispositionId !== undefined) {
      const currentUser = getCurrentUser();
      if (currentUser === null) {
        throw new Error('No current user found in session store.');
      }
      const { organizationId } = currentUser;

      const response = await deleteCallback(organizationId, nonce, dispositionId );

      await toastTransactionResponse(response, { dispositionIdToDelete: dispositionId }, mode);

      await refreshCallback(currentUser.organizationId);
    }
  }
    
  const createContent =
      <DialogHeader className="flex justify-center rounded-xl px-2 mt-4 transition-colors">
        <DialogTitle className="text-center text-lg text-foreground ">{t('settings.integrationsPages.mappings.createModalTitle')}</DialogTitle>
        <Label className="text-foreground">{t('settings.integrationsPages.mappings.modalInputLabel')}</Label>
        <Input
          required
          className="text-foreground"
          placeholder={t('settings.integrationsPages.mappings.modalInputPlaceholder')}
          type="text"
          onInput={handleNameInput}
        />
        {validationError.name != null && (
          <p className="text-xs text-error mt-1" data-qa-id="intent-title-error">{validationError.name}</p>
        )}
        <Label className="text-foreground">{t('settings.integrationsPages.mappings.modalSelectLabel')}</Label>
        <InputSelect
          placeholder={t('settings.integrationsPages.mappings.modalSelectPlaceholder')}
          clearable={true}
          options={dispositionTypeOptions}
          onValueChange={(value)=> {
            setCreateSelectedUpdateType(value);
            validateField('outcome', value);
          }}
        >
          {({
            options,
            placeholder,
            clearable,
            selectedValue,
            isPopoverOpen,
            setIsPopoverOpen,
            onClearAllOptions,
          }) => (
            <InputSelectTrigger
              options={options}
              placeholder={placeholder}
              clearable={clearable}
              disabled={options === null}
              selectedValue={selectedValue}
              isPopoverOpen={isPopoverOpen}
              setIsPopoverOpen={setIsPopoverOpen}
              onClearAllOptions={onClearAllOptions}
              className="w-3/4 rounded-md px-3 py-2 text-left bg-muted hover:bg-muted/50 border mt-2 mb-2"
            />
          )}
        </InputSelect>
        <DialogClose className="flex justify-center gap-4 mt-4">
          <Button className="cursor-pointer"
            disabled={validationError.name != null || validationError.outcome != null}
            onClick={() => {
              void createDisposition(createCallback);
            }}>
            {t('settings.integrationsPages.mappings.createDisposition')}
          </Button>
          <Button className="text-foreground" variant="outline" >{t('settings.integrationsPages.cancel')}</Button>
        </DialogClose>
      </DialogHeader>;

  const editContent =
        <DialogHeader className="flex justify-center rounded-xl px-2 mt-4 transition-colors">
          <DialogTitle className="text-foreground text-center text-lg">{t('settings.integrationsPages.mappings.editModalTitle')}</DialogTitle>
          <Label className="text-foreground">{t('settings.integrationsPages.mappings.modalInputLabel')}</Label>
          <Input
            placeholder={mappingValue}
            type="text"
            defaultValue={mappingValue}
            onFocus={()=> {
              validateField('name', mappingValue ?? '');
            }}
            onInput={handleNameInput}
            className={`text-foreground border w-full focus:outline-none focus:ring-1 ${
              validationError.name != null
                ? '!border-error focus:!ring-error'
                : 'border-input focus:ring-ring'
            }`}
          />
          {validationError.name != null && (
            <p className="text-xs text-error mt-1" data-qa-id="intent-title-error">{validationError.name}</p>
          )}
          <Label className="text-foreground">{t('settings.integrationsPages.mappings.modalSelectLabel')}</Label>
          <InputSelect
            placeholder={outcomeMap.find(outcome => outcome.value === currentDisposition?.outcome)?.label ?? 'Unknown'}
            clearable={true}
            options={dispositionTypeOptions}
            onValueChange={(value)=> {
              setSelectChanged(true);
              setUpdateSelectedUpdateType(value);
              validateField('outcome', value);
            }}
            value={selectedUpdateType}
          >
            {({
              options,
              placeholder,
              clearable,
              selectedValue,
              isPopoverOpen,
              setIsPopoverOpen,
              onClearAllOptions,
            }) => (
              <InputSelectTrigger
                options={options}
                placeholder={placeholder}
                clearable={clearable}
                disabled={options === null}
                selectedValue={selectedValue}
                isPopoverOpen={isPopoverOpen}
                setIsPopoverOpen={setIsPopoverOpen}
                onClearAllOptions={onClearAllOptions}
                className="w-3/4 rounded-md px-3 py-2 text-left bg-muted hover:bg-muted/50 border mt-2 mb-2"
              />
            )}
          </InputSelect>
          <DialogClose className="flex justify-center gap-4 mt-4">
            <Button className="cursor-pointer"
              disabled={(validationError.name != null && validationError.outcome != null) || (!inputChanged && !selectChanged)}
              onClick={() => {
                void updateDisposition(updateCallback);
              }}
            >{t('settings.integrationsPages.mappings.editDisposition')}</Button>
            <Button className="text-foreground" variant="outline" >{t('settings.integrationsPages.cancel')}</Button>
          </DialogClose>
        </DialogHeader>;
  
  const deleteContent = 
      <DialogHeader className="flex justify-center rounded-xl px-2 mt-4 transition-colors">
        <DialogTitle className="text-center text-lg text-foreground">{t('settings.integrationsPages.mappings.deleteModalTitle')}</DialogTitle>
        <div className="flex justify-center rounded-xl p-2 mt-4 transition-colors text-md">
          <p className="text-center text-foreground">{t('settings.integrationsPages.mappings.deleteDispositionWarning')}</p>
        </div>
        <DialogClose className="flex justify-center gap-4 mt-4">
          <Button size="sm" variant="destructive"
            onClick={()=> {
              void deleteDisposition(deleteCallback);
            }}
          >{t('settings.integrationsPages.mappings.deleteDisposition')}</Button>
          <Button size="sm" className="text-foreground" variant="outline" >{t('settings.integrationsPages.cancel')}</Button>
        </DialogClose>
      </DialogHeader>;
  
  function switchMode (mode: IntegrationMappingModalModes): React.JSX.Element {
    switch (mode) {
    case IntegrationMappingModalModes.Create: return createContent; 
    case IntegrationMappingModalModes.Edit: return editContent;
    case IntegrationMappingModalModes.Delete: return deleteContent;
    }
  }
    
  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className={`grid gap-6 ${dialogContainerStyles}`}>
        {switchMode(mode)}
      </DialogContent>
    </Dialog>
  );
}